import React, {FC, useMemo} from 'react';
import styled from 'styled-components';

import {TimeFormatsEnum} from '../../../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelTypesEnum} from '../../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ChannelModel} from '../../../../../../libs/shared/core/src/models/api/channelModel';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {PaletteColorsEnum} from '../../../../../../libs/shared/core/src/models/api/paletteModel';
import {
  CalendarsPanelId,
  EmlPanelId,
  makeCalendarsPanelId,
  MessagePanelId,
  ViewerPanelId,
} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {channelSupportsCalendarSync} from '../../../../../helpers/models/channelHelpers';
import {CalendarEventModel, CalendarEventStatusesEnum} from '../../../../../models/api/calendarEventModel';
import {
  CalendarEventVersionModel,
  CalendarEventVersionResponse,
} from '../../../../../models/api/calendarEventVersionModel';
import {CalendarVersionModel} from '../../../../../models/api/calendarVersionModel';
import {VisualParticipantModel} from '../../../../../models/api/visualParticipantModel';
import {PreviewCalendarsContainer} from '../../../../calendars/previewCalendarsContainer';
import {CalendarInviteAuthPrompt} from './calendarInviteAuthPrompt';
import {CalendarInviteViewerDetails} from './calendarInviteViewerDetails';
import {CalendarInviteViewerHeader} from './calendarInviteViewerHeader';
import {CalendarInviteViewerResponse} from './calendarInviteViewerResponse';

/*
 * Props.
 */

interface CalendarInviteViewerProps {
  panelId: ViewerPanelId | MessagePanelId | EmlPanelId;
  timeFormat: TimeFormatsEnum;
  calendarEvent: CalendarEventModel;
  calendarEventVersion: CalendarEventVersionModel;
  visualParticipants: ReadonlyArray<VisualParticipantModel>;
  hasICSAttachments: boolean;
  downloadICSAttachments: () => void;
  updateCalendarEventResponse: (response: CalendarEventVersionResponse) => void;
  replyChannel?: ChannelModel;
  calendarVersion?: CalendarVersionModel;
  response?: CalendarEventVersionResponse;
  reAuthChannel?: ChannelModel;
  reAuthInbox?: InboxModel;
  onGoToReAuthChannelSettingsClick: () => void;
  onAddToCalendar: () => void;
  hasIsCreatingFromOrphanEvent: boolean;
  hasIsCreatingFromOrphanEventOffice365: boolean;
  messageChannelProvider: ChannelTypesEnum | undefined;
}

/*
 * Style.
 */

const StyledInviteDiv = styled.div`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-column-gap: 10px;
`;

const StyledCalendarInviteIconDiv = styled.div`
  margin-top: 30px;
`;

const StyledCalendarInviteViewerDiv = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  margin-top: 16px;

  background-color: ${(p) => p.theme.foreground};
  border-radius: 16px;
  border: 1px solid ${(p) => p.theme.alphas.black20};
  ${(p) => boxShadow(p.theme, `0px 1px 6px ${p.theme.alphas.black20}`)};
`;

const StyledCalendarPreviewDiv = styled.div`
  margin: 0px 16px 14px 16px;
  height: 200px;

  border: 1px solid ${(p) => p.theme.greys.shade40};
  border-radius: 16px;

  overflow: hidden;
`;

const StyledCancelledBannerDiv = styled.div`
  margin: 0 16px 14px 16px;
  line-height: 30px;
  height: 30px;
  background-color: ${(p) => p.theme.palette.red.shade10};
  color: ${(p) => p.theme.palette.red.shade50};
  text-align: center;
  font-weight: ${fontWeights.semibold};
  font-size: 12px;
  border-radius: 16px;
`;

const StyledResponseWrapperDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  grid-column-gap: 10px;
  align-items: center;

  padding: 10px 10px 10px 16px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledIconWrapperDiv = styled.div`
  ${(p) => boxShadow(p.theme, `0 2px 9px ${p.theme.alphas.black50}`)};
  width: 30px;
  height: 30px;
  overflow: hidden;
  border-radius: 15px;
`;

/*
 * Component.
 */

export const CalendarInviteViewer: FC<CalendarInviteViewerProps> = (props) => {
  const theme = useUserTheme();
  const previewCalendarsPanelId = useMemo(() => makeCalendarsPanelId(), []);

  return (
    <StyledInviteDiv>
      <StyledCalendarInviteIconDiv>
        <StyledIconWrapperDiv>
          <Icon name="calendarSelected" color={theme.palette[PaletteColorsEnum.RED].shade40} />
        </StyledIconWrapperDiv>
      </StyledCalendarInviteIconDiv>
      <StyledCalendarInviteViewerDiv>
        <CalendarInviteViewerHeader
          calendarEvent={props.calendarEvent}
          calendarEventVersion={props.calendarEventVersion}
          downloadICSAttachments={props.downloadICSAttachments}
          hasICSAttachments={props.hasICSAttachments}
          timeFormat={props.timeFormat}
          messageChannelProvider={props.messageChannelProvider}
          onAddToCalendar={props.onAddToCalendar}
          hasIsCreatingFromOrphanEvent={props.hasIsCreatingFromOrphanEvent}
          hasIsCreatingFromOrphanEventOffice365={props.hasIsCreatingFromOrphanEventOffice365}
        />
        <CalendarInviteViewerDetails
          panelId={props.panelId}
          calendarEvent={props.calendarEvent}
          calendarEventVersion={props.calendarEventVersion}
          visualParticipants={props.visualParticipants}
        />
        {maybeRenderCancelledBanner(props.calendarEvent, props.calendarEventVersion)}
        {maybeRenderCalendarPreview(previewCalendarsPanelId, props)}
        {maybeRenderResponseSection(props)}
        {maybeRenderAuthPrompt(props)}
      </StyledCalendarInviteViewerDiv>
    </StyledInviteDiv>
  );
};

function maybeRenderCancelledBanner(
  calendarEvent: CalendarEventModel,
  calendarEventVersion: CalendarEventVersionModel,
) {
  if (calendarEvent.status === CalendarEventStatusesEnum.CANCELLED) {
    return (
      <StyledCancelledBannerDiv>
        <Trans id="invite-banner-cancelled">This event has been canceled</Trans>
      </StyledCancelledBannerDiv>
    );
  }

  if (calendarEventVersion.isRemoved) {
    return (
      <StyledCancelledBannerDiv>
        <Trans id="invite-banner-removed">This event has been removed from your calendar</Trans>
      </StyledCancelledBannerDiv>
    );
  }

  return null;
}

function maybeRenderCalendarPreview(calendarsPanelId: CalendarsPanelId, props: CalendarInviteViewerProps) {
  const {calendarVersion, calendarEventVersion, replyChannel} = props;

  const hasChannelWithCalendarSync = replyChannel && channelSupportsCalendarSync(replyChannel);
  if (!hasChannelWithCalendarSync || !calendarVersion || calendarEventVersion.isRemoved) {
    return null;
  }

  const {calendarEvent} = props;
  return (
    <StyledCalendarPreviewDiv>
      <PreviewCalendarsContainer
        panelId={calendarsPanelId}
        calendarEvent={calendarEvent}
        calendarEventVersion={calendarEventVersion}
        calendarVersion={calendarVersion}
      />
    </StyledCalendarPreviewDiv>
  );
}

function maybeRenderResponseSection(props: CalendarInviteViewerProps) {
  const {calendarEvent, calendarEventVersion, response, replyChannel} = props;
  if (
    !response ||
    !replyChannel ||
    calendarEvent.status === CalendarEventStatusesEnum.CANCELLED ||
    calendarEventVersion.isRemoved
  ) {
    return null;
  }

  return (
    <StyledResponseWrapperDiv>
      <CalendarInviteViewerResponse
        replyChannel={replyChannel}
        response={response}
        updateCalendarEventResponse={props.updateCalendarEventResponse}
        calendarEvent={calendarEvent}
      />
    </StyledResponseWrapperDiv>
  );
}

function maybeRenderAuthPrompt(props: CalendarInviteViewerProps) {
  const {reAuthChannel, reAuthInbox, onGoToReAuthChannelSettingsClick} = props;
  if (!reAuthChannel || !reAuthInbox) {
    return null;
  }

  return (
    <CalendarInviteAuthPrompt
      channel={reAuthChannel}
      onGoToReAuthChannelSettingsClick={onGoToReAuthChannelSettingsClick}
    />
  );
}
